import { useContext, useEffect } from 'react';
import AppContext from '../../../contexts/AppContext';
import { checkSession, forgetSavedSession } from '../../../utils/offlineSession';
import { endSession } from './SessionEndedDialog';

/** How often to try while the connection is up but the server did not answer. */
const RETRY_MS = 30000;

/**
 * SW2-2474 — checks a session restored offline with the server as soon as it
 * can be reached. Renders nothing.
 *
 * Until it has, the session is only what this device remembers: the user may
 * have been deactivated, or the login expired, in the meantime. The automatic
 * sync waits for this check (OfflineAutoSync), so nothing is uploaded first.
 *
 * - Confirmed: reload the app data from the server, which marks the session verified.
 * - Deactivated: delete the user's downloads, unsynced work included, say so,
 *   then log out.
 * - Otherwise logged out (expired, password reset pending): say so, log out,
 *   and keep the work on the device for when they sign in again.
 * - No answer about the session (offline, server error): stay as is and try
 *   again — on reconnect, and every RETRY_MS.
 */
const OfflineSessionGate = () => {
  const appContext = useContext(AppContext);
  const unverified = Boolean(appContext.get('sessionUnverified'));

  useEffect(() => {
    if (!unverified) return undefined;

    let running = false;
    let stopped = false;

    const verify = async () => {
      if (running || stopped || navigator.onLine === false) return;

      running = true;

      try {
        const session = await checkSession();

        if (stopped || session.state === 'unreachable') return;

        if (session.state === 'loggedIn') {
          // Fetches the profile again; if the connection drops meanwhile, the
          // session is restored as unverified again and this keeps trying.
          appContext.reloadInitialData();

          return;
        }

        stopped = true;

        if (session.otherTenant) {
          // Signed in to another tenant; this one needs verification. Start
          // again without the restored session — the app then asks them to
          // sign in here, as before — and leave the other tenant's cookie be.
          forgetSavedSession();
          window.location.reload();

          return;
        }

        await endSession(session.deactivation);
      } finally {
        running = false;
      }
    };

    verify();
    window.addEventListener('online', verify);
    const timer = window.setInterval(verify, RETRY_MS);

    return () => {
      stopped = true;
      window.removeEventListener('online', verify);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unverified]);

  return null;
};

export default OfflineSessionGate;
